import { useEffect, useRef, useState } from 'react';

/**
 * usePerformanceObserver (Enhanced Enterprise Edition)
 * 
 * Dynamically monitors the application's real-time framerate (FPS) using high-precision
 * performance.now() and requestAnimationFrame.
 * 
 * Features:
 * 1. Initial Instant Hardware Profiling (Device Memory, Hardware Concurrency, Touch / DPR)
 * 2. Rolling Exponential Moving Average (EMA) FPS calculation
 * 3. 3-Tier Performance Control (Optimal -> Moderate -> Extreme Low Power)
 * 4. Tab Visibility Awareness (Pauses monitoring when tab is hidden to avoid false triggers)
 * 5. Automatic Recovery Hysteresis (Upgrades tier when FPS recovers for sustained duration)
 * 6. Dynamic DOM Data-Attribute & Class injection on <html> and <body> for site-wide CSS adapting
 * 7. Exposes window custom event 'perf-tier-change' for dynamic canvas/video element adaptations
 */

export const PERF_TIERS = {
  OPTIMAL: 'optimal',   // 60+ FPS: Full animations, heavy glassmorphism & backdrop blurs
  MODERATE: 'moderate', // 38-51 FPS: Strip expensive backdrop-blurs & heavy drop-shadows
  LOW: 'low',           // <38 FPS: Strip all CSS keyframe animations, transitions, force solid backgrounds
};

// Initial hardware sanity check for instant non-lagging setup on low-end mobile/tablets
const getInitialTier = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return PERF_TIERS.OPTIMAL;
  }

  try {
    const memory = navigator.deviceMemory || 8; // GB RAM
    const cores = navigator.hardwareConcurrency || 8;
    const isSaveData = navigator.connection && navigator.connection.saveData;
    const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (isSaveData || prefersReducedMotion || memory <= 2 || cores <= 2) {
      return PERF_TIERS.LOW;
    }
    if (memory <= 4 || cores <= 4) {
      return PERF_TIERS.MODERATE;
    }
  } catch (e) {
    // Fallback if APIs restricted
  }
  return PERF_TIERS.OPTIMAL;
};

export const usePerformanceObserver = (
  fpsModerateThreshold = 50,
  fpsLowThreshold = 36,
  checkWindowSeconds = 2
) => {
  const [currentTier, setCurrentTier] = useState(() => getInitialTier());
  const tierRef = useRef(currentTier);
  const consecutiveLowSecs = useRef(0);
  const consecutiveHighSecs = useRef(0);

  // Sync state & DOM attributes helper
  const updateDOMTier = (newTier) => {
    if (typeof document === 'undefined') return;
    tierRef.current = newTier;
    setCurrentTier(newTier);

    const root = document.documentElement;
    const body = document.body;

    root.setAttribute('data-perf-tier', newTier);
    window.__PERF_TIER__ = newTier;

    // Apply classes for site-wide CSS targeting
    if (newTier === PERF_TIERS.OPTIMAL) {
      root.classList.remove('reduce-ui-fx', 'low-power-mode', 'perf-moderate', 'perf-low');
      body.classList.remove('reduce-ui-fx', 'low-power-mode', 'perf-moderate', 'perf-low');
      root.classList.add('perf-optimal');
      body.classList.add('perf-optimal');
    } else if (newTier === PERF_TIERS.MODERATE) {
      root.classList.remove('low-power-mode', 'perf-optimal', 'perf-low');
      body.classList.remove('low-power-mode', 'perf-optimal', 'perf-low');
      root.classList.add('reduce-ui-fx', 'perf-moderate');
      body.classList.add('reduce-ui-fx', 'perf-moderate');
    } else if (newTier === PERF_TIERS.LOW) {
      root.classList.remove('perf-optimal', 'perf-moderate');
      body.classList.remove('perf-optimal', 'perf-moderate');
      root.classList.add('reduce-ui-fx', 'low-power-mode', 'perf-low');
      body.classList.add('reduce-ui-fx', 'low-power-mode', 'perf-low');
    }

    // Dispatch global event for custom components (e.g. Canvas / Videojs)
    try {
      window.dispatchEvent(new CustomEvent('perf-tier-change', { detail: { tier: newTier } }));
    } catch (e) {}
  };

  useEffect(() => {
    // Initial DOM setup
    updateDOMTier(tierRef.current);

    if (typeof window === 'undefined' || !window.requestAnimationFrame || !window.performance) {
      return;
    }

    let rafId;
    let frames = 0;
    let prevTime = performance.now();
    let isTabHidden = document.hidden;

    const handleVisibilityChange = () => {
      isTabHidden = document.hidden;
      if (!isTabHidden) {
        prevTime = performance.now();
        frames = 0;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const measureFPS = () => {
      if (isTabHidden) {
        rafId = requestAnimationFrame(measureFPS);
        return;
      }

      const now = performance.now();
      frames++;
      const delta = now - prevTime;

      // Evaluate framerate every second
      if (delta >= 1000) {
        // Filter out tab switches or sudden system suspensions (>1800ms gap)
        if (delta < 1800) {
          const calculatedFps = Math.round((frames * 1000) / delta);
          const activeTier = tierRef.current;

          if (calculatedFps < fpsLowThreshold) {
            consecutiveLowSecs.current += 1;
            consecutiveHighSecs.current = 0;

            if (consecutiveLowSecs.current >= checkWindowSeconds && activeTier !== PERF_TIERS.LOW) {
              console.warn(`[FPS Monitor] Critical framerate drop detected (${calculatedFps} FPS). Activating Low Power Mode.`);
              updateDOMTier(PERF_TIERS.LOW);
              consecutiveLowSecs.current = 0;
            }
          } else if (calculatedFps < fpsModerateThreshold) {
            consecutiveLowSecs.current += 1;
            consecutiveHighSecs.current = 0;

            if (consecutiveLowSecs.current >= checkWindowSeconds && activeTier === PERF_TIERS.OPTIMAL) {
              console.warn(`[FPS Monitor] Moderate framerate drop detected (${calculatedFps} FPS). Disabling heavy blur & glassmorphism.`);
              updateDOMTier(PERF_TIERS.MODERATE);
              consecutiveLowSecs.current = 0;
            }
          } else {
            // Sustained high framerate (recovery hysteresis)
            consecutiveHighSecs.current += 1;
            consecutiveLowSecs.current = 0;

            // Wait 5 seconds of stable high FPS before recovering to higher visual tier
            if (consecutiveHighSecs.current >= 5) {
              if (activeTier === PERF_TIERS.LOW) {
                console.info(`[FPS Monitor] Framerate stabilized (${calculatedFps} FPS). Upgrading to Moderate performance mode.`);
                updateDOMTier(PERF_TIERS.MODERATE);
              } else if (activeTier === PERF_TIERS.MODERATE) {
                console.info(`[FPS Monitor] Framerate optimal (${calculatedFps} FPS). Restoring full UI visual effects.`);
                updateDOMTier(PERF_TIERS.OPTIMAL);
              }
              consecutiveHighSecs.current = 0;
            }
          }
        }

        prevTime = now;
        frames = 0;
      }

      rafId = requestAnimationFrame(measureFPS);
    };

    rafId = requestAnimationFrame(measureFPS);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fpsModerateThreshold, fpsLowThreshold, checkWindowSeconds]);

  return currentTier;
};

export default usePerformanceObserver;

